import { useMemo } from 'react';

import { CAST_AI } from '@cast/constants';
import { Dialog } from '@cast/design-system';
import { K8sProvider } from '@cast/shared-types';
import { Cluster } from '@cast/types';

import {
  useIsClusterDemoMode,
  useIsOpDemoMode,
} from '@cast/infrastructure-demo';
import { useAbility } from '@cast/shared-ability-data-access';
import { useActiveClusters } from 'features/organization/_hooks';
import { FeatureSelection } from 'hooks/dialogs/cluster/types';
import { useAssignCredentialsDialogV2 } from 'hooks/dialogs/cluster/useAssignCredentialsDialogV2';
import { useSecurityInsightsAgents } from 'hooks/queries/security-insights/useSecurityInsightsAgents';

import { SelectCluster } from '../_components/SelectCluster';

export type EnableCastFeaturesDialogProps = {
  filterClusterList?: boolean;
  shouldNavigateOnSuccess?: boolean;
  onSuccess?: (features: FeatureSelection) => void;
};

type Props = EnableCastFeaturesDialogProps & {
  onClose: () => void;
};

export const EnableCastFeaturesDialog = ({
  filterClusterList = true,
  shouldNavigateOnSuccess = true,
  onSuccess,
  onClose,
}: Props) => {
  const isOpDemoMode = useIsOpDemoMode();
  const isClusterDemoMode = useIsClusterDemoMode();

  const {
    clusters,
    isLoading: isClustersLoading,
    hasError,
    refetchClusters,
  } = useActiveClusters();
  const { agentsData, isLoading: isAgentsLoading } = useSecurityInsightsAgents({
    clusterIds: clusters.map((cluster) => cluster.id),
    enabled: !isClustersLoading && !!clusters.length,
  });
  const isCastAnywhereSecurityEnabled = useAbility().can(
    'view',
    'FeatureSecurityCastAnywhere'
  );

  const { openAssignCredentialsDialog } = useAssignCredentialsDialogV2({
    shouldNavigateOnSuccess,
    onSuccess,
  });

  const availableClusters = useMemo(() => {
    if (isOpDemoMode || isClusterDemoMode) {
      return [];
    }

    if (!filterClusterList) {
      return clusters;
    }

    return clusters.filter(
      (cluster) =>
        (cluster.providerType !== K8sProvider.ANYWHERE ||
          isCastAnywhereSecurityEnabled) &&
        !agentsData?.agentStatuses?.[cluster.id]
    );
  }, [
    isOpDemoMode,
    isClusterDemoMode,
    filterClusterList,
    clusters,
    isCastAnywhereSecurityEnabled,
    agentsData?.agentStatuses,
  ]);

  const handleSelect = (cluster: Cluster) => {
    onClose();
    openAssignCredentialsDialog({ cluster });
  };

  return (
    <Dialog
      open
      testId="enable-cast-features-dialog"
      disableCloseOnBackdropClick
      onClose={onClose}
      PaperProps={{
        sx: {
          overflow: 'visible',
        },
      }}
    >
      <SelectCluster
        data={availableClusters}
        title={`Enable ${CAST_AI} features`}
        isLoading={isClustersLoading || isAgentsLoading}
        hasError={hasError}
        refetchClusters={refetchClusters}
        onClose={onClose}
        onSelect={handleSelect}
      />
    </Dialog>
  );
};
